import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import {
  Plus, CircleNotch, Funnel, ArrowLeft, Package, Lightning,
  CheckCircle, XCircle, Clock, Eye, PencilSimple, ArrowClockwise,
  Storefront, Upload, CaretDown, X, Trash, Check, ChartBar,
  TrendUp, Download, Users
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-500/10 text-gray-400' },
  submitted: { label: 'In Review', className: 'bg-amber-500/10 text-amber-400' },
  in_review: { label: 'In Review', className: 'bg-amber-500/10 text-amber-400' },
  approved: { label: 'Approved', className: 'bg-blue-500/10 text-blue-400' },
  published: { label: 'Published', className: 'bg-emerald-500/10 text-emerald-400' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-400' },
  suspended: { label: 'Suspended', className: 'bg-orange-500/10 text-orange-400' },
};

const AUTH_MODELS = ['api_key', 'oauth2', 'bearer_token', 'basic_auth', 'platform_managed', 'none'];

// ─── Capability Selector ──────────────────────────────────────────
const CapabilitySelector = ({ selected, onChange }) => {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchCaps = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/app-store/capabilities`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setCapabilities(data.capabilities || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchCaps();
  }, []);

  const filtered = capabilities.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.id?.toLowerCase().includes(search.toLowerCase()) ||
    c.category?.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(c => {
    const cat = c.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(c);
  });

  const toggle = (capId) => {
    if (selected.includes(capId)) {
      onChange(selected.filter(id => id !== capId));
    } else {
      onChange([...selected, capId]);
    }
  };

  return (
    <div>
      <label className="text-xs text-[var(--text-muted)] mb-1.5 block">Capabilities (from Registry) *</label>
      {/* Selected pills */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map(id => {
          const cap = capabilities.find(c => c.id === id);
          return (
            <span key={id} className="px-2 py-0.5 bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] rounded text-xs flex items-center gap-1">
              {cap?.name || id.replace(/_/g, ' ')}
              <X size={10} className="cursor-pointer hover:text-red-400" onClick={() => toggle(id)} />
            </span>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-xs text-[var(--text-secondary)] text-left flex items-center justify-between hover:border-[var(--brand-primary)]/40"
        data-testid="capability-selector-toggle"
      >
        <span>{selected.length > 0 ? `${selected.length} selected` : 'Select capabilities...'}</span>
        <CaretDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="mt-1 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-lg max-h-56 overflow-y-auto" data-testid="capability-dropdown">
          <div className="sticky top-0 bg-[var(--surface-2)] p-2 border-b border-[var(--border-default)]">
            <input
              placeholder="Search capabilities..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 bg-[var(--surface-3)] border border-[var(--border-default)] rounded text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none"
              data-testid="capability-search"
            />
          </div>
          {loading ? (
            <div className="flex justify-center py-4"><CircleNotch size={16} className="animate-spin text-[var(--brand-primary)]" /></div>
          ) : (
            Object.entries(grouped).map(([cat, caps]) => (
              <div key={cat}>
                <p className="px-3 py-1.5 text-[9px] uppercase text-[var(--text-muted)] tracking-wider bg-[var(--surface-1)]">{cat}</p>
                {caps.map(cap => (
                  <div
                    key={cap.id}
                    onClick={() => toggle(cap.id)}
                    className={`px-3 py-2 flex items-center gap-2 cursor-pointer text-xs hover:bg-[var(--surface-3)] transition-colors ${selected.includes(cap.id) ? 'text-[var(--brand-primary)]' : 'text-[var(--text-secondary)]'}`}
                  >
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${selected.includes(cap.id) ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)]' : 'border-[var(--border-default)]'}`}>
                      {selected.includes(cap.id) && <Check size={10} weight="bold" className="text-white" />}
                    </div>
                    <span className="font-medium">{cap.name}</span>
                    {cap.side_effects && <span className="text-[9px] text-amber-400 ml-auto">side effect</span>}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Submission Form Modal ────────────────────────────────────────
const SubmissionFormModal = ({ onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState({
    name: '', description: '', short_description: '', category: '',
    capabilities: [], auth_model: 'api_key', developer_name: '', company_name: '',
    icon_url: '', docs_url: '', support_url: '', permissions: '',
    provider_config: '{}',
    ...initialData,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (submitNow = false) => {
    if (!form.name.trim()) return setError('Name is required');
    if (form.capabilities.length === 0) return setError('Select at least one capability');
    setError('');
    setSubmitting(true);

    let providerConfig = {};
    try {
      if (form.provider_config.trim()) providerConfig = JSON.parse(form.provider_config);
    } catch { setError('Invalid JSON in Provider Config'); setSubmitting(false); return; }

    try {
      const body = {
        name: form.name, description: form.description, short_description: form.short_description,
        category: form.category, capabilities: form.capabilities, auth_model: form.auth_model,
        developer_name: form.developer_name, company_name: form.company_name,
        icon_url: form.icon_url, docs_url: form.docs_url, support_url: form.support_url,
        permissions: form.permissions ? form.permissions.split(',').map(s => s.trim()).filter(Boolean) : [],
        provider_config: providerConfig,
        submit_now: submitNow,
      };
      await onSubmit(body);
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--brand-primary)]";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="app-submission-form">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <Package size={20} className="text-[var(--brand-primary)]" />
            {initialData ? 'Edit App Submission' : 'Submit New App'}
          </h2>

          {error && <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-400">{error}</div>}

          <input data-testid="app-name-input" placeholder="App Name *" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className={inputCls} />
          <input data-testid="app-short-desc-input" placeholder="Short Description" value={form.short_description} onChange={e => setForm(f => ({...f, short_description: e.target.value}))} className={inputCls} />
          <textarea data-testid="app-desc-input" placeholder="Full Description" rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className={inputCls + ' resize-none'} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Category</label>
              <input placeholder="e.g. Communication" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className={inputCls} data-testid="app-category-input" />
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] mb-1 block">Auth Model</label>
              <select value={form.auth_model} onChange={e => setForm(f => ({...f, auth_model: e.target.value}))} className={inputCls} data-testid="app-auth-select">
                {AUTH_MODELS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>

          <CapabilitySelector selected={form.capabilities} onChange={caps => setForm(f => ({...f, capabilities: caps}))} />

          <div className="grid grid-cols-2 gap-3">
            <input data-testid="app-dev-name" placeholder="Developer Name" value={form.developer_name} onChange={e => setForm(f => ({...f, developer_name: e.target.value}))} className={inputCls} />
            <input data-testid="app-company" placeholder="Company Name" value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} className={inputCls} />
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Icon URL (512x512 recommended)</label>
            <input data-testid="app-icon-url" placeholder="https://..." value={form.icon_url} onChange={e => setForm(f => ({...f, icon_url: e.target.value}))} className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Documentation URL" value={form.docs_url} onChange={e => setForm(f => ({...f, docs_url: e.target.value}))} className={inputCls} />
            <input placeholder="Support URL" value={form.support_url} onChange={e => setForm(f => ({...f, support_url: e.target.value}))} className={inputCls} />
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Permissions / Scopes (comma-separated)</label>
            <input placeholder="e.g. chat:write, channels:read" value={form.permissions} onChange={e => setForm(f => ({...f, permissions: e.target.value}))} className={inputCls} data-testid="app-permissions" />
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] mb-1 block">Provider Config (JSON)</label>
            <textarea data-testid="app-provider-config" rows={3} placeholder='{"base_url": "https://api.example.com"}' value={form.provider_config} onChange={e => setForm(f => ({...f, provider_config: e.target.value}))} className={inputCls + ' font-mono text-xs resize-none'} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">Cancel</button>
            <button onClick={() => handleSubmit(false)} disabled={submitting} className="px-4 py-2 border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" data-testid="save-draft-btn">
              Save Draft
            </button>
            <button onClick={() => handleSubmit(true)} disabled={submitting} className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center gap-1.5" data-testid="submit-app-btn">
              {submitting && <CircleNotch size={14} className="animate-spin" />}
              Submit for Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Developer Analytics Summary ──────────────────────────────────
const DevAnalyticsSummary = ({ stats }) => {
  if (!stats || stats.length === 0) return null;
  const totalInstalls = stats.reduce((a, s) => a + s.install_count, 0);
  const totalInvocations = stats.reduce((a, s) => a + s.invocation_count, 0);
  const totalSuccesses = stats.reduce((a, s) => a + s.success_count, 0);
  const totalWorkspaces = stats.reduce((a, s) => a + s.workspace_count, 0);
  const successRate = totalInvocations > 0 ? Math.round(totalSuccesses / totalInvocations * 1000) / 10 : null;
  const lastUsed = stats.filter(s => s.last_used_at).sort((a, b) => (b.last_used_at || '').localeCompare(a.last_used_at || ''))[0]?.last_used_at;

  return (
    <div className="grid grid-cols-5 gap-3 mb-6" data-testid="dev-analytics-summary">
      {[
        { label: 'Total Installs', value: totalInstalls, icon: Download, color: 'text-blue-400' },
        { label: 'Workspaces', value: totalWorkspaces, icon: Users, color: 'text-purple-400' },
        { label: 'Invocations', value: totalInvocations, icon: Lightning, color: 'text-[var(--brand-primary)]' },
        { label: 'Success Rate', value: successRate != null ? `${successRate}%` : 'N/A', icon: TrendUp, color: successRate >= 90 ? 'text-emerald-400' : successRate >= 70 ? 'text-amber-400' : 'text-[var(--text-muted)]' },
        { label: 'Last Active', value: lastUsed ? new Date(lastUsed).toLocaleDateString() : 'Never', icon: Clock, color: 'text-[var(--text-muted)]' },
      ].map(s => (
        <div key={s.label} className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-3 text-center">
          <s.icon size={16} weight="bold" className={`mx-auto mb-1 ${s.color}`} />
          <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
          <p className="text-[9px] text-[var(--text-muted)] mt-0.5">{s.label}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────
const MyAppSubmissions = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [devStats, setDevStats] = useState([]);

  const fetchSubs = useCallback(async () => {
    try {
      setLoading(true);
      const q = filter ? `?status=${filter}` : '';
      const [subsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/app-store/submissions${q}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/app-store/analytics/developer`, { credentials: 'include' }),
      ]);
      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubmissions(data.submissions || []);
        setTotal(data.total || 0);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setDevStats(data.apps || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const getAppStats = (subId) => devStats.find(s => s.app_id === subId);

  const handleCreate = async (body) => {
    const res = await fetch(`${BACKEND_URL}/api/app-store/submissions`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || 'Failed to create submission');
    }
    setShowForm(false);
    fetchSubs();
  };

  return (
    <div className="min-h-screen bg-[var(--surface-0)] p-6" data-testid="my-app-submissions-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/app-store')} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><ArrowLeft size={18} /></button>
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-primary)]">My App Submissions</h1>
              <p className="text-sm text-[var(--text-muted)] mt-1">Submit apps and track their review status.</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded-lg text-sm font-medium hover:bg-[var(--brand-primary-hover)] transition-colors flex items-center gap-1.5"
            data-testid="new-app-submission-btn"
          >
            <Plus size={16} weight="bold" />New App
          </button>
        </div>

        {/* Developer Analytics Summary */}
        <DevAnalyticsSummary stats={devStats} />

        {/* Filter */}
        <div className="flex items-center gap-3 mb-4">
          <Funnel size={16} className="text-[var(--text-muted)]" />
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="px-3 py-1.5 bg-[var(--surface-2)] border border-[var(--border-default)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-primary)]"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <span className="text-xs text-[var(--text-muted)]">{total} submission{total !== 1 ? 's' : ''}</span>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)]">
            <Package size={40} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-[var(--text-secondary)] text-sm">No app submissions yet</p>
            <button onClick={() => setShowForm(true)} className="mt-3 text-sm text-[var(--brand-primary)] hover:underline">Submit your first app</button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="submissions-list">
            {submissions.map(sub => {
              const status = STATUS_CONFIG[sub.status] || STATUS_CONFIG.draft;
              const appStats = getAppStats(sub.submission_id);
              return (
                <div key={sub.submission_id} className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-4 hover:border-[var(--brand-primary)]/30 transition-all" data-testid={`submission-${sub.submission_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{sub.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${status.className}`}>{status.label}</span>
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-1">{sub.short_description || sub.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
                          <Lightning size={10} />{sub.capabilities?.length || 0} capabilities
                        </span>
                        <span className="text-[10px] text-[var(--text-muted)]">{sub.category || 'Uncategorized'}</span>
                        {sub.trust_class && (
                          <span className="text-[10px] text-blue-400">{sub.trust_class.replace(/_/g, ' ')}</span>
                        )}
                      </div>
                    </div>
                    {/* Per-App Analytics Mini Stats */}
                    {appStats && (appStats.install_count > 0 || appStats.invocation_count > 0) && (
                      <div className="flex items-center gap-4 ml-4 flex-shrink-0" data-testid={`stats-${sub.submission_id}`}>
                        <div className="text-center">
                          <p className="text-xs font-bold text-blue-400">{appStats.install_count}</p>
                          <p className="text-[8px] text-[var(--text-muted)]">installs</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-bold text-[var(--brand-primary)]">{appStats.invocation_count}</p>
                          <p className="text-[8px] text-[var(--text-muted)]">invocations</p>
                        </div>
                        {appStats.success_rate != null && (
                          <div className="text-center">
                            <p className={`text-xs font-bold ${appStats.success_rate >= 90 ? 'text-emerald-400' : appStats.success_rate >= 70 ? 'text-amber-400' : 'text-red-400'}`}>{appStats.success_rate}%</p>
                            <p className="text-[8px] text-[var(--text-muted)]">success</p>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 ml-2">
                      {sub.status === 'rejected' && sub.review_notes && (
                        <span className="text-[10px] text-red-400 max-w-[140px] truncate" title={sub.review_notes}>{sub.review_notes}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showForm && <SubmissionFormModal onClose={() => setShowForm(false)} onSubmit={handleCreate} />}
      </div>
    </div>
  );
};

export default MyAppSubmissions;
